import { PostJobSteps } from 'Douleutaras/views/job/post-job/hooks/post-job';
import { STEPS_ALLOWED_TO_RESTORE_FROM_STORAGE } from 'Douleutaras/views/job/post-external-job/constants';

export interface DecidedInitialValues {
  form: {
    questions?: {};
    location?: string;
    address?: string;
  };
  initialQuestionsHistory: Array<number>;
  lastStoredStep: PostJobSteps;
}

export type QuestionMapping = Record<`${number}`, string | Array<string>>;

// Validates an object mapping numeric keys to string values or arrays of strings.
export const validateQuestionMapping = (data: QuestionMapping): boolean => {
  // Must be a plain object
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return false;
  }

  // Iterate over each entry
  for (const [key, value] of Object.entries(data)) {
    // Validate key: must be numeric
    if (!/^\d+$/.test(key)) {
      return false;
    }

    // Validate value: either a non-empty string or an array of non-empty strings
    if (typeof value === 'string') {
      if (value.length === 0) {
        return false;
      }
    } else if (Array.isArray(value)) {
      if (
        value.length === 0 ||
        !value.every(v => typeof v === 'string' && v.length > 0)
      ) {
        return false;
      }
    } else {
      return false;
    }
  }

  return true;
};

/**
 * Initialize questions from with the following priority:
 * 1. From URL
 * 2. From local storage
 *
 * Will retrieve the following:
 * - initialValues
 * - initialQuestionsHistory
 */
export const decideInitialValues = (searchParams, categoryId): DecidedInitialValues => {
  const storedJobForm = typeof localStorage !== 'undefined' ? JSON.parse(localStorage.getItem('job-form')) : {};
  const locationFromUrl = searchParams.get('loc');
  const storedCategoryForm = storedJobForm?.[categoryId];
  const initialQuestion = searchParams.get('initialQuestions') !== 'undefined' ?
    JSON.parse(searchParams.get('initialQuestions'))
    : undefined;

  const hasInitialQuestionsFromUrl = Boolean(initialQuestion);
  const hasLocationFromUrl = Boolean(locationFromUrl);

  const hasStoredCategoryForm = Boolean(storedCategoryForm);
  const hasQuestionsFromStorage = Boolean(hasStoredCategoryForm && storedCategoryForm.form?.questions);
  const hasLocationFromStorage = Boolean(hasStoredCategoryForm && storedCategoryForm.location);
  const hasAddressFromStorage = Boolean(hasStoredCategoryForm && storedCategoryForm.address);
  const hasInitialQuestionsHistory = Boolean(hasStoredCategoryForm && storedCategoryForm.questionsStepHistory);
  const hasCurrentStepFromStorage = Boolean(hasStoredCategoryForm && storedCategoryForm.currentStep);

  let questions;
  let location;
  let address;
  let initialQuestionsHistory;

  // Questions
  switch (true) {
    case hasInitialQuestionsFromUrl && hasQuestionsFromStorage:
      questions = { ...storedCategoryForm.form.questions, ...{ [initialQuestion.options_id]: initialQuestion.selection_id }};
      break;
    case hasInitialQuestionsFromUrl:
      questions = { [initialQuestion.options_id]: initialQuestion.selection_id };
      break;
    case hasQuestionsFromStorage:
      questions = storedCategoryForm.form.questions;
      break;
    default:
      break;
  }

  // Location
  switch (true) {
    // We cant get only location from URL, we also need address for it to work properly
    case hasLocationFromStorage:
      location = storedCategoryForm.location;
      break;
    default:
      break;
  }

  // Address
  switch (true) {
    case hasAddressFromStorage:
      address = storedCategoryForm.address;
      break;
    default:
      break;
  }

  // initialQuestionsHistory
  switch (true) {
    case hasInitialQuestionsFromUrl && hasInitialQuestionsHistory:
      initialQuestionsHistory = storedCategoryForm.questionsStepHistory;
      break;
    case hasInitialQuestionsFromUrl && Boolean(initialQuestion.question_id):
      initialQuestionsHistory = [ initialQuestion.question_id ];
      break;
    case hasInitialQuestionsHistory:
      initialQuestionsHistory = storedCategoryForm.questionsStepHistory;
      break;
    default:
  }

  const currentStoredStep = hasCurrentStepFromStorage ? storedCategoryForm.currentStep : '';

  const returnValuesForm: {
    questions?: {};
    location?: string;
    address?: string;
  } = {};

  if (questions) {
    returnValuesForm.questions = questions;
  }

  if (validatePoint(location)) {
    returnValuesForm.location = location;
  }

  if (validateAddress(address)) {
    returnValuesForm.address = address;
  }

  // Only jump to last stored step when we have questions answered
  const lastStoredStep =
    (hasCurrentStepFromStorage
      && STEPS_ALLOWED_TO_RESTORE_FROM_STORAGE.includes(currentStoredStep)
      && Boolean(returnValuesForm.questions) // Has questions
    )
      ? storedJobForm[categoryId].currentStep
      : STEPS_ALLOWED_TO_RESTORE_FROM_STORAGE.slice(0, -1);

  return {
    form: returnValuesForm,
    initialQuestionsHistory: validateNumberArray(initialQuestionsHistory) ? initialQuestionsHistory : undefined,
    lastStoredStep
  };
};

// Validates the following properties
export const validateInitialValueProperties = form => {
  return form.hasOwnProperty('questions')
    && form.hasOwnProperty('location')
    && form.hasOwnProperty('address');
};

// Validates a POINT string in the form "POINT(<lon><sep><lat>)" with ranges:
// lon: -180..180, lat: -90..90, where <sep> is either '+' or a single space
export const validatePoint = (input: unknown): boolean => {
  // Must be a string
  if (typeof input !== 'string') {
    return false;
  }

  // Allow either '+' or space as separator between lon and lat
  const match = input.match(
    /^POINT\(\s*([+-]?\d+(?:\.\d+)?)\s*(?:\+|\s)\s*([+-]?\d+(?:\.\d+)?)\s*\)$/
  );
  if (!match) {
    return false;
  }

  const lon = Number(match[1]);
  const lat = Number(match[2]);

  // Must be finite numbers
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
    return false;
  }

  // Range checks
  if (lon < -180 || lon > 180) {
    return false;
  }
  if (lat < -90 || lat > 90) {
    return false;
  }

  return true;
};

// Validates an array of finite numbers (no NaN, Infinity, or non-numbers).
export const validateNumberArray = (input: unknown): boolean => {
  // Must be an array
  if (!Array.isArray(input)) {
    return false;
  }

  // Must contain only finite numbers
  if (!input.every(v => typeof v === 'number' && Number.isFinite(v))) {
    return false;
  }

  return true;
};

export const validateAddress = (input: unknown): boolean => {
  // Must be a string
  if (typeof input !== 'string') {
    return false;
  }

  const trimmed = input.trim();

  // Require at least 3 characters and reject overly long addresses (>200 chars)
  if (trimmed.length < 3 || trimmed.length > 200) {
    return false;
  }

  // Allow Unicode letters (e.g., Greek), numbers, spaces, and common punctuation like commas, periods, dash, #, and apostrophes
  // @ts-ignore
  const unicodeAddressRegex = /^[\p{L}\p{N}\s.,#'’\-]+$/u;
  if (!unicodeAddressRegex.test(trimmed)) {
    return false;
  }

  return true;
};

// Priority: explicit prop, then a numeric `?category=`, then whatever the form already picked.
export const getCategoryId = (
  initialCategoryId: string | undefined,
  searchParams: URLSearchParams,
  formCategoryId: string | undefined
): string | undefined => {
  if (initialCategoryId) {
    return initialCategoryId;
  }

  if (searchParams.has('category') && !isNaN(parseInt(searchParams.get('category'), 0))) {
    return searchParams.get('category');
  }

  return formCategoryId;
};
